import { FormatListBulletedOutlined } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import {
	FormActions,
	FormCard,
	FormField,
	FormInput,
	FormSectionTitle,
	FormSelect,
} from '@practium/ui';
import { APPLICATION_METHOD_OPTIONS } from '../../constants/maintenance.constants';
import { usePlanningForm } from '../../hooks/usePlanningForm';
import type { FertilizationPlanAdvice } from '../../types';
import { MonthPicker } from '../MonthPicker/MonthPicker';
import { SimpleDataTable } from '../Tables/SimpleDataTable/SimpleDataTable';
import { PanelActions } from '../PanelActions/PanelActions';
import tabStyles from '../Tabs/TabPanel.module.css';
import { FertilizerPackModal } from './FertilizerPackModal';
import { PlanningPackField } from './PlanningPackField';
import styles from './PlanningPanel.module.css';

interface PlanningPanelProps {
	advice: FertilizationPlanAdvice;
}

export const PlanningPanel = ({ advice }: PlanningPanelProps) => {
	const {
		form,
		errors,
		set,
		planMonths,
		isView,
		hasPersisted,
		hasCalculated,
		isCalculating,
		showIrrigationFields,
		demandView,
		setDemandView,
		fertilizerDemand,
		nutritionDemand,
		packOptions,
		selectedPack,
		packModal,
		openPackModal,
		closePackModal,
		handlePackSaved,
		handleDuplicatePack,
		handleClearPack,
		handleCalculate,
		handleCancel,
		handleEdit,
		methodLabel,
		startMonthLabel,
		endMonthLabel,
		packLabel,
	} = usePlanningForm({ advice });

	const [revealIrrigation, setRevealIrrigation] = useState(false);
	const irrigationWasVisible = useRef(false);

	useEffect(() => {
		if (!showIrrigationFields) {
			irrigationWasVisible.current = false;
			setRevealIrrigation(false);
			return;
		}
		if (!irrigationWasVisible.current && !isView) {
			setRevealIrrigation(true);
		}
		irrigationWasVisible.current = true;
	}, [isView, showIrrigationFields]);

	const irrigationRevealClass = revealIrrigation ? styles.reveal : '';

	return (
		<div className={tabStyles.panel}>
			<FormCard icon={<FormatListBulletedOutlined />} title="Informações Gerais">
				<div className={tabStyles.form}>
					<div className={tabStyles.section}>
						<FormSectionTitle>Planejamento geral</FormSectionTitle>
						<div className={tabStyles.sectionFields}>
							<div className={tabStyles.row3}>
								<FormField label="Método de aplicação" required error={errors.method}>
									{isView ? (
										<span className={tabStyles.viewText}>{methodLabel}</span>
									) : (
										<FormSelect
											value={form.method}
											onChange={(value) => set('method', value)}
											options={APPLICATION_METHOD_OPTIONS}
											placeholder="Selecione o método"
											hasError={!!errors.method}
										/>
									)}
								</FormField>
								<FormField label="Mês inicial" required error={errors.startMonth}>
									{isView ? (
										<span className={tabStyles.viewText}>{startMonthLabel}</span>
									) : (
										<MonthPicker
											value={form.startMonth}
											onChange={(value) => set('startMonth', value)}
											placeholder="Ex.: Janeiro"
											allowedMonths={planMonths}
											hasError={!!errors.startMonth}
										/>
									)}
								</FormField>
								<FormField label="Mês final" required error={errors.endMonth}>
									{isView ? (
										<span className={tabStyles.viewText}>{endMonthLabel}</span>
									) : (
										<MonthPicker
											value={form.endMonth}
											onChange={(value) => set('endMonth', value)}
											placeholder="Ex.: Dezembro"
											allowedMonths={planMonths}
											hasError={!!errors.endMonth}
										/>
									)}
								</FormField>
							</div>

							{showIrrigationFields && (
								<>
									<div
										className={`${tabStyles.row3} ${irrigationRevealClass}`}
										onAnimationEnd={() => setRevealIrrigation(false)}
									>
										<FormField label="Volume da caixa de injeção" required error={errors.vbox}>
											{isView ? (
												<span className={tabStyles.viewText}>
													{form.vbox ? `${form.vbox} L` : '—'}
												</span>
											) : (
												<FormInput
													type="number"
													min="0"
													value={form.vbox}
													onChange={(e) => set('vbox', e.target.value)}
													suffix="L"
													hasError={!!errors.vbox}
												/>
											)}
										</FormField>
										<FormField label="Concentração permitida" required error={errors.cemMax}>
											{isView ? (
												<span className={tabStyles.viewText}>
													{form.cemMax ? `${form.cemMax} g/L` : '—'}
												</span>
											) : (
												<FormInput
													type="number"
													min="0"
													step="0.1"
													value={form.cemMax}
													onChange={(e) => set('cemMax', e.target.value)}
													suffix="g/L"
													hasError={!!errors.cemMax}
												/>
											)}
										</FormField>
										<FormField label="Vazão do injetor" required error={errors.injectorFlow}>
											{isView ? (
												<span className={tabStyles.viewText}>
													{form.injectorFlow ? `${form.injectorFlow} L` : '—'}
												</span>
											) : (
												<FormInput
													type="number"
													min="0"
													value={form.injectorFlow}
													onChange={(e) => set('injectorFlow', e.target.value)}
													suffix="L"
													hasError={!!errors.injectorFlow}
												/>
											)}
										</FormField>
									</div>
									<div className={`${tabStyles.row3} ${irrigationRevealClass}`}>
										<FormField label="Fração de sais" required error={errors.saltFraction}>
											{isView ? (
												<span className={tabStyles.viewText}>
													{form.saltFraction ? `${form.saltFraction} %` : '—'}
												</span>
											) : (
												<FormInput
													type="number"
													min="0"
													max="100"
													step="1"
													value={form.saltFraction}
													onChange={(e) => set('saltFraction', e.target.value)}
													suffix="%"
													hasError={!!errors.saltFraction}
												/>
											)}
										</FormField>
										<FormField label="Eventos desejados por mês" error={errors.desiredEventsPerMonth}>
											{isView ? (
												<span className={tabStyles.viewText}>
													{form.desiredEventsPerMonth || '—'}
												</span>
											) : (
												<FormInput
													type="number"
													min="1"
													max="12"
													step="1"
													value={form.desiredEventsPerMonth}
													onChange={(e) => set('desiredEventsPerMonth', e.target.value)}
													hasError={!!errors.desiredEventsPerMonth}
												/>
											)}
										</FormField>
									</div>
									<PlanningPackField
										isView={isView}
										packId={form.packId}
										packLabel={packLabel}
										packOptions={packOptions}
										selectedPack={selectedPack}
										error={errors.packId}
										onChange={(value) => set('packId', value)}
										onOpenModal={openPackModal}
										onDuplicate={handleDuplicatePack}
										onClear={handleClearPack}
									/>
								</>
							)}
						</div>
					</div>

					{hasCalculated && (
						<>
							<div className={tabStyles.section}>
								<div className={styles.sectionHeader}>
									<FormSectionTitle>Demanda de fertilizantes</FormSectionTitle>
									<div className={styles.viewTabs}>
										<button
											type="button"
											className={`${styles.viewTab} ${demandView === 'hectare' ? styles.viewTabActive : ''}`}
											onClick={() => setDemandView('hectare')}
										>
											Por hectare
										</button>
										<button
											type="button"
											className={`${styles.viewTab} ${demandView === 'sector' ? styles.viewTabActive : ''}`}
											onClick={() => setDemandView('sector')}
										>
											Por setor
										</button>
									</div>
								</div>
								<SimpleDataTable
									columns={fertilizerDemand.columns}
									rows={fertilizerDemand.rows}
								/>
							</div>
							{nutritionDemand.rows.length > 0 && (
								<div className={tabStyles.section}>
									<FormSectionTitle>Demanda nutricional atendida</FormSectionTitle>
									<SimpleDataTable
										columns={nutritionDemand.columns}
										rows={nutritionDemand.rows}
									/>
								</div>
							)}
						</>
					)}
				</div>
			</FormCard>

			<PanelActions>
				{isView ? (
					<FormActions mode="view" onEdit={handleEdit} />
				) : (
					<FormActions
						onCancel={hasPersisted ? handleCancel : undefined}
						onSecondary={handleCalculate}
						secondaryText={hasPersisted ? 'Recalcular' : 'Calcular'}
						secondaryDisabled={isCalculating}
						secondaryLoading={isCalculating}
					/>
				)}
			</PanelActions>

			<FertilizerPackModal
				isOpen={packModal.open}
				mode={packModal.mode}
				pack={packModal.pack}
				onClose={closePackModal}
				onSaved={handlePackSaved}
				onDeleted={handleClearPack}
			/>
		</div>
	);
};
